'use client';

import { forwardRef } from 'react';
import { cn } from '@/utils/mergeClassName';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

type ScrollAreaProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> &
  Pick<ScrollBarProps, 'orientation' | 'scrollbarClassName' | 'thumbClassName'>;

export const ScrollArea = forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(
  (
    { children, className, scrollbarClassName, thumbClassName, orientation = 'vertical', ...props },
    forwardedRef
  ) => (
    <ScrollAreaPrimitive.Root
      ref={forwardedRef}
      className={cn('relative overflow-hidden', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport className="size-full rounded-[inherit]">
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar
        orientation={orientation}
        scrollbarClassName={scrollbarClassName}
        thumbClassName={thumbClassName}
      />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
);

type ScrollBarProps = React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.ScrollAreaScrollbar
> & {
  scrollbarClassName?: string;
  thumbClassName?: string;
};

const ScrollBar = forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  ScrollBarProps
>(({ scrollbarClassName, thumbClassName, orientation = 'vertical', ...props }, forwardedRef) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={forwardedRef}
    orientation={orientation}
    className={cn(
      'duration-[160ms] flex touch-none select-none bg-grey-300/30 transition-colors ease-out data-[orientation=horizontal]:h-1 data-[orientation=vertical]:w-1 data-[orientation=horizontal]:flex-col',
      scrollbarClassName
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb
      className={cn(
        "relative z-50 flex-1 rounded-sm bg-grey-300 before:absolute before:left-1/2 before:top-1/2 before:size-full before:min-h-1 before:min-w-1 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']",
        thumbClassName
      )}
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
